// Graph Explorer layout — synchronous d3-force pass, then freeze.
//
// The canvas must be STABLE: a live simulation jitters and re-shuffles on
// every expansion, which reads as noise in an evidence tool. So layout runs
// as a one-shot: pin what's already placed (fx/fy), seed new nodes near an
// already-placed neighbor (so expansions grow outward from their parent
// instead of teleporting in), tick a fixed number of iterations, return
// plain positions. React Flow renders the frozen result; users can still
// drag nodes manually afterwards.

import {
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
} from 'd3-force';

const TICKS = 300;

/**
 * @param {Array<{id: string, kind: string}>} nodes
 * @param {Array<{source: string, target: string}>} edges
 * @param {Object<string, {x: number, y: number}>} pinned  already-placed positions
 * @param {string} seedId  the seed node — pinned at the origin on first layout
 * @returns {Object<string, {x: number, y: number}>}
 */
export function layoutGraph(nodes, edges, pinned = {}, seedId = null) {
  if (!nodes.length) return {};

  const placed = { ...pinned };
  if (seedId && !placed[seedId]) placed[seedId] = { x: 0, y: 0 };

  // Adjacency for placing newcomers next to their first placed neighbor.
  const neighborOf = {};
  for (const e of edges) {
    (neighborOf[e.source] ||= []).push(e.target);
    (neighborOf[e.target] ||= []).push(e.source);
  }

  const simNodes = nodes.map((n, i) => {
    const p = placed[n.id];
    if (p) return { id: n.id, x: p.x, y: p.y, fx: p.x, fy: p.y };
    const anchor = (neighborOf[n.id] || []).map((m) => placed[m]).find(Boolean);
    // Fan newcomers around their anchor (or the origin) on a deterministic
    // angle-by-index ring so the initial state is already roughly radial.
    const angle = (i * 2.399963) % (2 * Math.PI); // golden angle — no clumping
    const base = anchor || { x: 0, y: 0 };
    return { id: n.id, x: base.x + 180 * Math.cos(angle), y: base.y + 180 * Math.sin(angle) };
  });

  const simEdges = edges
    .filter((e) => e.source !== e.target)
    .map((e) => ({ source: e.source, target: e.target }));

  const sim = forceSimulation(simNodes)
    .force('charge', forceManyBody().strength(-600).distanceMax(900))
    .force('link', forceLink(simEdges).id((d) => d.id).distance(190).strength(0.35))
    .force('collide', forceCollide(80))
    .force('x', forceX(0).strength(0.03))
    .force('y', forceY(0).strength(0.03))
    .stop();

  for (let i = 0; i < TICKS; i++) sim.tick();

  const out = {};
  for (const n of simNodes) out[n.id] = { x: Math.round(n.x), y: Math.round(n.y) };
  return out;
}
